import { Link } from 'react-router-dom'
import {
  ArrowRight,
  ClipboardCheck,
  FileText,
  Megaphone,
  Receipt,
  Vote,
  type LucideIcon,
} from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { useMemberBalance } from '@/hooks/use-billing'
import { useOpenPollsAwaitingVote } from '@/hooks/use-polls'
import { useServiceRequests } from '@/hooks/use-requests'
import { useAnnouncements } from '@/hooks/use-announcements'
import { useDocuments } from '@/hooks/use-documents'
import { useDocumentRequests } from '@/hooks/use-document-requests'
import { formatKES } from '@/lib/auth'
import { formatDate } from '@/lib/utils'
import { htmlToExcerpt } from '@riverpark/utils/html'

/** How far back a "recent" widget (announcements, documents) looks before treating an item as
 * stale and no longer dashboard-worthy — this is a notice board, not an archive. */
const RECENT_WINDOW_DAYS = 14

function isRecent(unixSeconds: number): boolean {
  const ageMs = Date.now() - unixSeconds * 1000
  return ageMs <= RECENT_WINDOW_DAYS * 24 * 60 * 60 * 1000
}

function WidgetCard({
  icon: Icon,
  title,
  viewAllTo,
  children,
}: {
  icon: LucideIcon
  title: string
  viewAllTo: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-card border border-border-2 bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border-2 px-5 py-3">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-muted-foreground" />
          <h2 className="font-serif text-lg text-ink">{title}</h2>
        </div>
        <Link
          to={viewAllTo}
          className="flex items-center gap-1 font-sans text-xs font-medium text-muted-foreground hover:text-primary"
        >
          View all
          <ArrowRight size={12} />
        </Link>
      </div>
      <div className="divide-y divide-border-2">{children}</div>
    </div>
  )
}

function WidgetRow({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="block px-5 py-3.5 transition-colors hover:bg-cream-2">
      {children}
    </Link>
  )
}

export function MemberDashboard() {
  const { data: balanceData, isLoading: balanceLoading } = useMemberBalance()
  const { data: pollsData, isLoading: pollsLoading } = useOpenPollsAwaitingVote()
  const { data: requestsData, isLoading: requestsLoading } = useServiceRequests({ mine: true })
  const { data: announcementsData, isLoading: announcementsLoading } = useAnnouncements({
    limit: 10,
  })
  const { data: documentsData, isLoading: documentsLoading } = useDocuments({ limit: 10 })
  const { data: docRequestsData, isLoading: docRequestsLoading } = useDocumentRequests()

  const anyLoading =
    balanceLoading ||
    pollsLoading ||
    requestsLoading ||
    announcementsLoading ||
    documentsLoading ||
    docRequestsLoading

  const balance = balanceData?.data.balance ?? 0
  const polls = pollsData ?? []
  const openRequests = (requestsData?.data ?? [])
    .filter((r) => r.status === 'open' || r.status === 'in_progress')
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 3)
  const recentAnnouncements = (announcementsData?.data ?? [])
    .filter((a) => isRecent(a.publishedAt ?? a.createdAt))
    .slice(0, 3)
  const recentDocuments = (documentsData?.data ?? [])
    .filter((d) => isRecent(d.createdAt))
    .slice(0, 3)
  const outstandingDocRequests = (docRequestsData?.data ?? [])
    .filter((r) => r.status !== 'approved' && r.status !== 'cancelled')
    .slice(0, 3)

  const hasBalance = balance > 0
  const hasPolls = polls.length > 0
  const hasRequests = openRequests.length > 0
  const hasAnnouncements = recentAnnouncements.length > 0
  const hasDocuments = recentDocuments.length > 0
  const hasDocRequests = outstandingDocRequests.length > 0
  const somethingToShow =
    hasBalance || hasPolls || hasRequests || hasAnnouncements || hasDocuments || hasDocRequests

  return (
    <PageShell>
      <PageHeader label="Overview" title="Dashboard" description="What needs your attention." />

      {anyLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : !somethingToShow ? (
        <div className="rounded-card border border-border-2 bg-card px-6 py-10 text-center">
          <p className="font-serif text-lg text-ink">You're all caught up</p>
          <p className="mt-1 font-sans text-sm text-muted-foreground">
            No outstanding balance, open votes, or new updates right now.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {hasBalance && (
            <div className="flex flex-col gap-3 rounded-card border border-status-overdue-bg bg-status-overdue-bg/40 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-status-overdue-text">
                  Outstanding balance
                </p>
                <p className="mt-1 font-serif text-2xl text-ink">{formatKES(balance)}</p>
              </div>
              <Link
                to="/bills"
                className="flex items-center gap-1.5 self-start rounded bg-ink px-4 py-2 font-sans text-sm font-medium text-cream transition-opacity hover:opacity-90 sm:self-auto"
              >
                View bills &amp; pay
                <ArrowRight size={14} />
              </Link>
            </div>
          )}

          {hasPolls && (
            <WidgetCard icon={Vote} title="Awaiting your vote" viewAllTo="/polls">
              {polls.slice(0, 3).map((poll) => (
                <WidgetRow key={poll.id} to={`/polls/${poll.id}`}>
                  <p className="font-sans text-sm font-medium text-ink">{poll.title}</p>
                  <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                    Voting closes {formatDate(new Date(poll.endAt * 1000))}
                  </p>
                </WidgetRow>
              ))}
            </WidgetCard>
          )}

          {hasRequests && (
            <WidgetCard icon={Receipt} title="Your open requests" viewAllTo="/requests">
              {openRequests.map((req) => (
                <WidgetRow key={req.id} to={`/requests?open=${req.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-sans text-sm font-medium text-ink">{req.subject}</p>
                    <span className="shrink-0 font-sans text-xs text-muted-foreground">
                      {req.status === 'in_progress' ? 'In progress' : 'Open'}
                    </span>
                  </div>
                  <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                    Updated {formatDate(new Date(req.updatedAt * 1000))}
                  </p>
                </WidgetRow>
              ))}
            </WidgetCard>
          )}

          {hasDocRequests && (
            <WidgetCard
              icon={ClipboardCheck}
              title="Documents needed"
              viewAllTo="/documents?tab=requests"
            >
              {outstandingDocRequests.map((req) => (
                <WidgetRow key={req.id} to="/documents?tab=requests">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-sans text-sm font-medium text-ink">{req.title}</p>
                    <span className="shrink-0 font-sans text-xs text-muted-foreground">
                      {req.status === 'needs_revision' ? 'Needs revision' : req.status}
                    </span>
                  </div>
                  <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                    Plot {req.plotNumber} ·{' '}
                    {req.items.filter((i) => i.status !== 'approved').length} outstanding
                  </p>
                </WidgetRow>
              ))}
            </WidgetCard>
          )}

          {hasAnnouncements && (
            <WidgetCard icon={Megaphone} title="Recent notices" viewAllTo="/announcements">
              {recentAnnouncements.map((a) => (
                <WidgetRow key={a.id} to={`/announcements/${a.id}`}>
                  <p className="font-sans text-sm font-medium text-ink">{a.title}</p>
                  <p className="mt-0.5 line-clamp-1 font-sans text-xs text-muted-foreground">
                    {htmlToExcerpt(a.body, 120)}
                  </p>
                </WidgetRow>
              ))}
            </WidgetCard>
          )}

          {hasDocuments && (
            <WidgetCard icon={FileText} title="New documents" viewAllTo="/documents">
              {recentDocuments.map((doc) => (
                <WidgetRow key={doc.id} to="/documents">
                  <p className="font-sans text-sm font-medium text-ink">{doc.title}</p>
                  <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                    Added {formatDate(new Date(doc.createdAt * 1000))}
                  </p>
                </WidgetRow>
              ))}
            </WidgetCard>
          )}
        </div>
      )}
    </PageShell>
  )
}
